import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, Loader2, Copy, Share2, Save, CheckCircle2, ExternalLink, MapPin, Calendar, PoundSterling, Ticket } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

// Distinct badge colour per provider — keeps the source obvious at a glance.
const SOURCE_STYLE = {
  ticketmaster: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', label: 'Ticketmaster' },
  ticketweb: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-200', label: 'Ticketweb' },
  skiddle: { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-200', label: 'Skiddle' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const EventCard = ({ item, onSaved }) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(item.already_saved);
  const style = SOURCE_STYLE[item.source] || SOURCE_STYLE.ticketmaster;

  const copyLink = async () => {
    await navigator.clipboard.writeText(item.affiliate_link);
    toast({ title: 'Affiliate link copied', description: 'Ready to paste anywhere.' });
  };

  const shareToX = () => {
    const text = `${item.title}${item.venue ? ` at ${item.venue}` : ''}${item.price ? ` — ${item.price}` : ''}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(item.affiliate_link)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(item.affiliate_link)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const saveToSite = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/events/finder/save`, {
        provider: item.provider,
        event_id: item.event_id,
        title: item.title,
        start_date: item.start_date,
        venue: item.venue,
        address: item.address,
        image_url: item.image_url,
        affiliate_link: item.affiliate_link,
        raw_link: item.raw_link,
        description: item.description,
        price: item.price,
      }, { headers: auth() });
      setSaved(true);
      onSaved?.(item);
      toast({
        title: data.already_existed ? 'Already saved' : 'Saved to site',
        description: data.already_existed ? 'This event was previously imported.' : `Published as /events/${data.slug}`,
      });
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col" data-testid={`finder-card-${item.provider}-${item.event_id}`}>
      <div className="relative aspect-video bg-slate-100">
        {item.image_url ? (
          <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300"><Ticket size={40} /></div>
        )}
        <span className={`absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${style.bg} ${style.text} border ${style.border}`}>
          {style.label}
        </span>
        {item.price && (
          <span className="absolute top-2 right-2 text-xs font-bold px-2 py-1 rounded bg-slate-900/80 text-white backdrop-blur">
            {item.price}
          </span>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <h3 className="text-base font-bold text-slate-900 leading-tight line-clamp-2 mb-2" title={item.title}>{item.title}</h3>
        <div className="text-xs text-slate-600 space-y-1 mb-3">
          {item.venue && (
            <div className="flex items-center gap-1"><MapPin size={12} className="text-slate-400" />{item.venue}{item.address ? ` · ${item.address}` : ''}</div>
          )}
          <div className="flex items-center gap-1"><Calendar size={12} className="text-slate-400" />{fmtDate(item.start_date)}</div>
        </div>

        <div className="mt-auto grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="finder-copy-link" className="text-xs">
            <Copy size={12} className="mr-1" /> Copy link
          </Button>
          <Button variant="outline" size="sm" onClick={shareToX} data-testid="finder-share-x" className="text-xs">
            <Share2 size={12} className="mr-1" /> Share X
          </Button>
          <Button variant="outline" size="sm" onClick={shareToFacebook} data-testid="finder-share-fb" className="text-xs">
            <Share2 size={12} className="mr-1" /> Share FB
          </Button>
          {saved ? (
            <Button variant="outline" size="sm" disabled className="text-xs text-emerald-700 border-emerald-300 bg-emerald-50" data-testid="finder-saved">
              <CheckCircle2 size={12} className="mr-1" /> On site
            </Button>
          ) : (
            <Button size="sm" onClick={saveToSite} disabled={saving} className="text-xs bg-cyan-600 hover:bg-cyan-700" data-testid="finder-save-btn">
              {saving ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Save size={12} className="mr-1" />}
              {saving ? 'Saving…' : 'Save to site'}
            </Button>
          )}
        </div>

        <a href={item.affiliate_link} target="_blank" rel="noopener noreferrer" className="mt-2 text-[11px] text-slate-400 hover:text-slate-600 inline-flex items-center gap-1 truncate" title={item.affiliate_link}>
          <ExternalLink size={10} /> Open on {style.label}
        </a>
      </div>
    </div>
  );
};

const EventFinderPage = () => {
  const [q, setQ] = useState('');
  const [days, setDays] = useState(90);
  const [radius, setRadius] = useState(30);
  const [source, setSource] = useState('all');
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);

  const runSearch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/events/finder`, {
        headers: auth(),
        params: { q, days, radius, source, size: 30 },
      });
      setItems(data.items || []);
      setCount(data.count);
    } catch (err) {
      setItems([]);
      setCount(0);
      console.error('Finder search failed', err);
    } finally { setLoading(false); }
  }, [q, days, radius, source]);

  useEffect(() => {
    // Initial search on page load — surfaces upcoming events straight away
    // so the admin has something to look at without typing. Deliberately
    // omitting runSearch from deps so typing in the search box doesn't
    // trigger a query per keystroke — the admin drives it via the button.
    runSearch();
     
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    runSearch();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="event-finder-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Ticket className="text-cyan-500" size={26} /> Event Finder
        </h1>
        <p className="text-slate-600 mt-1">
          Live search across <b>Ticketmaster</b>, <b>Ticketweb</b> and <b>Skiddle</b> — every link comes back with your affiliate ID baked in.
        </p>
      </div>

      <form onSubmit={onSubmit} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm mb-6" data-testid="finder-search-panel">
        <div className="grid md:grid-cols-[1fr_140px_140px_180px_auto] gap-2 items-end">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Keyword</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="e.g. Oasis, King Tut's, Paisley…"
                className="pl-9"
                data-testid="finder-q-input"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Radius (mi)</label>
            <Input type="number" min={1} max={100} value={radius} onChange={(e) => setRadius(Number(e.target.value))} data-testid="finder-radius-input" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Days ahead</label>
            <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="finder-days-input" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Source</label>
            <select value={source} onChange={(e) => setSource(e.target.value)} className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm bg-white" data-testid="finder-source-select">
              <option value="all">All providers</option>
              <option value="ticketmaster">Ticketmaster / Ticketweb</option>
              <option value="skiddle">Skiddle only</option>
            </select>
          </div>
          <Button type="submit" disabled={loading} className="bg-cyan-600 hover:bg-cyan-700 h-10" data-testid="finder-search-btn">
            {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Search size={14} className="mr-1" />}
            {loading ? 'Searching…' : 'Search'}
          </Button>
        </div>
      </form>

      {count !== null && !loading && (
        <p className="text-xs text-slate-500 mb-3" data-testid="finder-result-count">
          {count} result{count === 1 ? '' : 's'}{q ? ` for "${q}"` : ''} · within {radius}mi of Paisley · next {days} days
        </p>
      )}

      {loading && (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={32} /></div>
      )}

      {!loading && items.length === 0 && count !== null && (
        <div className="text-center py-16 text-slate-500" data-testid="finder-empty">
          <PoundSterling className="mx-auto mb-2 text-slate-300" size={40} />
          <p>Nothing matches those filters — try broadening the search or ticking a different source.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="finder-results-grid">
          {items.map((it) => (
            <EventCard key={`${it.provider}-${it.event_id}`} item={it} onSaved={runSearch} />
          ))}
        </div>
      )}
    </div>
  );
};

export default EventFinderPage;
